import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { SKILL_NAMES } from 'js/src/agent/skills/skillNames';
import { recipeCopy } from 'js/src/agent/skills/recipeCopy';
import type { Skill } from '../types';

export const createBoldCaptionsVideoSkill: Skill = {
    name: SKILL_NAMES.createBoldCaptionsVideo,
    description:
        'Large captions burned across fast-cutting full-bleed media, with an AI voiceover and no other text on screen. Use for social-native, caption-led videos where the spoken words are the whole visual.',
    kind: 'video',
    surfaces: ['sketch'],
    dashboard: {
        ...recipeCopy(SKILL_NAMES.createBoldCaptionsVideo),
        preview: 'captions-bold',
        frames: ['captions-bold-1', 'captions-bold-2', 'captions-bold-3', 'captions-bold-4'],
    },
    instructions: `# Create Bold Captions Video
For a social-native video where **the captions are the design**: an AI
voiceover drives large captions across the whole runtime, and behind them
full-bleed media cuts fast. Nothing else carries words — no headlines, no
subtitles, no lower thirds. The viewer reads the narration as it is spoken
and watches the media change underneath it.

Load \`${SKILL_NAMES.voiceoverVideoBasics}\` first; it owns the fundamentals this format
is built on (how overlays cover the transcript, how word ranges are anchored,
and coverage repair). This recipe adds the caption-led structure on top and
does not repeat any of it.

Two rules here override the general defaults: captions are **on for the whole
video** rather than off-by-default, and every scene is **media-only** rather
than a mix of text and media designs.

## HARD RULE: the captions are the only text on screen
Every scene in this video must be a **media-only scene** — the clip or photo
is the only thing in the design. No headline, no subtitle, no icon, no shape,
no badge. Not "text kept short," not "text that echoes the narration." None.
The caption track carries 100% of the words, and a text asset sitting
alongside it means the same sentence appears twice on screen at once, which
is the defect this format exists to avoid.

This rule overrides scene-design variety — if the only media-only option
available is repetitive, use it anyway rather than reaching for a design that
shows text.

Category labels like "media" or "fullbleed" are a hint, not a guarantee — some
designs filed under them still carry a text or icon asset. Don't trust the
category name alone. For every candidate design:
1. Add it (or check it via \`${TOOL_NAMES.listSceneDesigns}\`/\`${TOOL_NAMES.getVideoState}\`) and look
   at its actual asset list.
2. Zero TEXT assets and zero icon/graphic assets — genuinely just media — and
   it's usable.
3. If every option carries a TEXT or icon asset, pick the cleanest full-bleed
   one and force each non-media asset empty/hidden (blank text, no icon set)
   so nothing renders. Then confirm with \`${TOOL_NAMES.getVideoState}\` that those
   assets are actually empty, and run \`${TOOL_NAMES.reviewSceneImages}\` on the scene so
   a leftover asset that does render gets flagged.

**Never reach for an animated-captions design** (the ANIMATED_CAPTIONS display
group). Those render their own captions, which would double up against the
caption track this format depends on.

## Setup
Use \`${SKILL_NAMES.gatherRequirements}\` to get necessary info, skipping anything already
provided. Use that skill's general suggestions, but also specifically make sure we get:
- **caption style** — that skill asks this one after the video is created, not up front
- The topic or source content the narration is written from
- Ideal duration — this format skews short; don't default to a long preset
- Any specific media that needs to appear
- Voice (search according to the instructions below to offer good options).
  Set \`require_voiceover: true\` on that question — captions only exist on a
  voicedriven video, so a video without narration isn't this format at all.

Two overrides to that skill for this format:
- **Aspect ratio**: ask it as usual (the picker takes no default), but if the
  user doesn't pick one, use \`fourfive\` (4:5) — a social-native near-square suits
  this format. Either way it's passed to \`${TOOL_NAMES.createBlankVideo}\` when you
  create the video, not applied afterwards.
- **Don't ask whether they want captions.** They are the format; they are on.

## Narration and music
- **An AI voiceover is the spine of this format** — captions only exist on a
  voicedriven video, so there is always narration. Search \`${TOOL_NAMES.searchVoices}\`
  for a voice with energy and clear diction; captions track the speech
  word-for-word, so a mumbled or over-processed read reads badly on screen.
- Music is optional but suits the format. If you add a track
  (\`${TOOL_NAMES.searchMusic}\`, then \`${TOOL_NAMES.setMusic}\`), set its volume
  (\`${TOOL_NAMES.setMusicVolume}\`) low enough that the voiceover stays clearly
  intelligible — the captions and the voice have to agree.

## Script — written to be read as much as heard
- Tone is **direct and punchy**. Short, self-contained sentences; every one of
  them is going on screen verbatim as a caption.
- **Avoid long unbroken sentences.** A sentence that takes eight seconds to say
  becomes a caption block the viewer is still reading when the media has cut
  twice. Break ideas at natural clause boundaries so each lands inside a
  scene's span.
- Skip anything that doesn't read well as text: parentheticals, nested
  clauses, spelled-out URLs, and filler openers all look worse on screen than
  they sound.

## Structure — one continuous narration, media cutting underneath
- The video is **one voiceover scene group** carrying the whole script, with
  media-only overlays covering it end to end (\`${TOOL_NAMES.addVoiceoverScene}\`, every
  overlay for the group in a single call).
- **Every word of the transcript is covered by an overlay.** An uncovered
  stretch renders as raw waveform behind the captions, which is a defect here.
  Check \`${TOOL_NAMES.lintVideo}\`'s \`overlay_coverage\` before finishing.
- Don't split the group into sections or drop silent cards between parts —
  this format is one continuous run. The rhythm comes from the media cuts,
  not from structural breaks.

## Captions — the signature element
- **Turn captions on for the whole video** (\`${TOOL_NAMES.setCaptions}\` with
  \`enabled: true\`). Captions are a whole-video setting, so this is one call —
  and a later \`${TOOL_NAMES.setCaptions}\` that turns them off would silently
  undo the format.
- **Make them genuinely big.** Call \`${TOOL_NAMES.setCaptions}\` with no arguments
  first: it reports the settable range and, separately, the largest size that
  fits on one line with the current design. **Go past that one-line size** —
  this format wants captions that dominate the frame, and wrapping onto two
  or three lines is normal here, not a defect. Read the applied value back
  (\`${TOOL_NAMES.getVideoState}\`) rather than trusting the request.
- **Then look at it** (\`${TOOL_NAMES.imageAsk}\`) and judge the result, since past the
  one-line size the only real limit is the frame. Back the size off when the
  captions crowd the media out or push past the safe area; otherwise leave
  them large. A caption track that reads as ordinary subtitles is the defect
  this format exists to avoid.
- **Position them center** (\`position: 'center'\`) unless the user asks
  otherwise. Center-frame is what makes this read as a caption-led piece
  rather than a subtitled one, and it keeps the text off the bottom edge where
  social platforms overlay their own UI.
- Set \`text_color\` and \`spoken_word_color\` only when the template's inherited
  caption styling doesn't hold up against the media — see Styling below.

## Asset timing — fast cuts, but every caption stays readable
- **Anchor each overlay's \`words\` range to a natural phrase in the
  narration.** The captions render from the transcript regardless, so the word
  ranges here are choosing where the *media* cuts, not what text appears.
- **Target roughly 2-3 seconds of spoken duration per scene, with about 3
  seconds as a hard ceiling.** This is a genuinely fast cut rate and it is
  correct here — the momentum is the point. After anchoring the ranges, check
  how long each actually takes to say (\`${TOOL_NAMES.getVideoState}\` or
  \`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\`); anything running past about 3
  seconds should be split at the next clause boundary into two overlays.
- **Never split a range mid-phrase.** A cut landing inside a phrase reads as a
  mistake, and because the captions run continuously across the cut the
  mismatch is visible. Split at boundaries the speech already has.
- Don't chase the cut rate below about 2 seconds. Captions this large need a
  beat to be read, and media flickering faster than the words fights them.
- When one idea spans two consecutive overlays, keep the media direction
  consistent across them so it reads as one moment rather than two unrelated
  cuts.

## Media
- **Every scene is full-bleed.** One media asset filling the frame, edge to
  edge — no split-screens, no collages, no insets, no letterboxing. The
  captions sit over an uninterrupted image.
- The clip or shot must obviously fit what is being said at that moment — a
  vaguely-related stock clip is worse than no media, and it is especially
  exposed here because the caption underneath names exactly what the viewer
  should be seeing.
- Load \`${SKILL_NAMES.findStockMedia}\` before your first stock search, and
  \`${SKILL_NAMES.findUserMedia}\` when the user has their own media. Load
  \`${SKILL_NAMES.findDamMedia}\` too when a DAM is connected — brand content likely
  lives there.
- **Reframe every asset so nothing important sits behind the captions**
  (\`${SKILL_NAMES.adjustMedia}\` skill, then \`${TOOL_NAMES.editMedia}\`). With captions centered
  and large, the middle band of the frame is spoken for: faces, product
  details, and any text burned into the footage need to sit clear of it.
- **Prefer motion over stills.** A fast-cutting format built from static
  photos reads as a slideshow; video clips carry the pace. Where a still is
  the right image, give it a pan or zoom rather than leaving it frozen.
- Don't reuse the same clip across scenes — a repeat is immediately obvious at
  this cut rate.

## Styling
- Colors and styling come from the template or the user's brand kit — apply a
  kit when one exists or the user references their brand
  (\`${SKILL_NAMES.applyBrandKit}\` skill); don't hand-pick color overrides
  unprompted.
- **Legibility over the media is the one thing worth overriding for.** Large
  centered captions sit on top of whatever the footage is doing, and a bright
  or busy shot can swallow them. Check with \`${TOOL_NAMES.validateContrast}\`, and
  fix a failure by dimming the media on that scene (\`${TOOL_NAMES.editMedia}\`) or by
  setting a \`text_color\` that holds up — load \`${SKILL_NAMES.fixTextContrast}\` when
  it needs more than a single adjustment.
- If the template's caption design highlights the spoken word, leave that on —
  it reinforces the read-along feel this format is going for.

## Finishing
Load \`${SKILL_NAMES.reviewVideo}\` for the standard pass. On top of that, confirm:
- **Captions are on** and stayed on for the whole video (\`${TOOL_NAMES.getVideoState}\`) —
  nothing later in the build turned them off.
- **The caption text size actually landed large** and centered — read the
  applied value back rather than trusting the call, and confirm by eye
  (\`${TOOL_NAMES.imageAsk}\`) that the captions dominate the frame rather than
  sitting in it like ordinary subtitles.
- **Every scene is media-only**: check each one's real asset list, not its
  category label. Zero visible TEXT assets, zero visible icon/graphic assets.
  A scene showing a headline alongside the captions is a defect no matter how
  it looks in the preview.
- **No animated-captions design was used anywhere** — that would render a
  second set of captions on top of the caption track.
- **Every scene is a single full-bleed media asset** — no split-screens,
  collages, or insets.
- **Cut rate holds to about 2-3 seconds of speech per scene**, with nothing
  running meaningfully past 3 (\`${TOOL_NAMES.lintVideo}\`'s \`scene_reading_time\`), and
  no cut landing mid-phrase.
- **The whole transcript is covered** — \`${TOOL_NAMES.lintVideo}\`'s
  \`overlay_coverage\` reports no uncovered words, so no stretch of raw waveform
  plays behind the captions.
- **The captions are legible over their media everywhere**
  (\`${TOOL_NAMES.validateContrast}\`), and nothing important in any shot sits hidden
  behind the center caption band.
- A voiceover is attached, and if music was added it doesn't compete with it.
- The aspect ratio matches what the user confirmed in Setup.`,
};
